import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, User, Bot, Minimize2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface LeadInfo {
  name?: string;
  email?: string;
  phone?: string;
}

const quickReplies = [
  { label: "Get a quote", message: "I'd like to get a quote for your services" },
  { label: "Book a call", message: "I'd like to book a consultation call" },
  { label: "View services", message: "What services do you offer?" },
  { label: "Pricing info", message: "Can you tell me about your pricing?" },
];

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [leadInfo, setLeadInfo] = useState<LeadInfo>({});
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [stickyBarVisible, setStickyBarVisible] = useState(false);
  const [showIntroForm, setShowIntroForm] = useState(true);
  const [introName, setIntroName] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track if sticky CTA bar is visible to adjust position
  useEffect(() => {
    const handleScroll = () => {
      const isDismissed = sessionStorage.getItem("sticky_cta_dismissed") === "true";
      setStickyBarVisible(!isDismissed && window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Check initial state
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current && !showIntroForm) {
      inputRef.current.focus();
    }
  }, [isOpen, showIntroForm]);

  // Start chat with personalized greeting after intro form
  const startChatWithName = () => {
    const name = introName.trim();
    if (name) {
      setLeadInfo({ name });
    }
    setShowIntroForm(false);
    setHasGreeted(true);
    
    const greeting = name 
      ? `Hi ${name}! 👋 Thanks for reaching out. I'm Val, your digital marketing assistant. What brings you here today? Are you looking to grow your online presence, get more leads, or something else?`
      : "Hi there! 👋 I'm Val, your digital marketing assistant. What brings you here today? Are you looking to grow your online presence, get more leads, or something else?";
    
    setMessages([
      {
        id: "greeting",
        role: "assistant",
        content: greeting,
      },
    ]);
  };

  // Extract lead info from messages
  const extractLeadInfo = useCallback((text: string): Partial<LeadInfo> => {
    const info: Partial<LeadInfo> = {};
    
    // Email pattern
    const emailMatch = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    if (emailMatch) info.email = emailMatch[0];
    
    // Phone pattern
    const phoneMatch = text.match(/(?:\+1)?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/);
    if (phoneMatch) info.phone = phoneMatch[0].replace(/\D/g, "");
    
    return info;
  }, []);

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    // Hide quick replies after first user message
    setShowQuickReplies(false);

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      content: textToSend,
    };

    // Check for lead info in user's message
    const extractedInfo = extractLeadInfo(textToSend);
    const updatedLeadInfo = { ...leadInfo, ...extractedInfo };
    setLeadInfo(updatedLeadInfo);

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("chat-assistant", {
        body: {
          messages: [...messages, userMessage].map((m) => ({
            role: m.role,
            content: m.content,
          })),
          leadInfo: updatedLeadInfo.email ? updatedLeadInfo : null,
        },
      });

      if (error) throw error;

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: data.message,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: "assistant",
          content: "I'm having trouble connecting. Please try again or reach out to us at ValentineDigitalMarketing@gmail.com",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReply = (message: string) => {
    sendMessage(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleIntroKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      startChatWithName();
    }
  };

  const handleSendClick = () => {
    sendMessage();
  };

  const toggleChat = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={toggleChat}
        className={cn(
          "fixed right-4 z-50 p-4 rounded-full transition-all duration-500 group",
          "bg-gradient-to-br from-accent to-accent/80 text-accent-foreground",
          "shadow-xl shadow-accent/30 hover:shadow-2xl hover:shadow-accent/40",
          "ring-4 ring-accent/20 hover:ring-accent/40",
          "hover:scale-110 active:scale-95",
          "before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:-translate-x-full hover:before:translate-x-full before:transition-transform before:duration-700",
          "overflow-hidden",
          isOpen && !isMinimized && "scale-0 opacity-0",
          stickyBarVisible ? "bottom-20" : "bottom-4"
        )}
        aria-label="Open chat"
      >
        <MessageCircle className="h-6 w-6 relative z-10 group-hover:rotate-12 transition-transform duration-300" />
        {/* Notification dot */}
        {!isOpen && (
          <span className="absolute -top-1 -right-1 h-4 w-4 bg-destructive rounded-full animate-pulse ring-2 ring-background shadow-lg shadow-destructive/50" />
        )}
      </button>

      {/* Chat Window */}
      <div
        className={cn(
          "fixed right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)] transition-all duration-500 origin-bottom-right",
          isOpen ? "scale-100 opacity-100" : "scale-0 opacity-0 pointer-events-none",
          isMinimized && "h-14",
          stickyBarVisible ? "bottom-20" : "bottom-4"
        )}
      >
        <div className="relative bg-background/95 backdrop-blur-xl border border-border/50 rounded-2xl shadow-2xl shadow-primary/10 overflow-hidden flex flex-col h-[500px] max-h-[calc(100vh-6rem)]">
          {/* Shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_3s_infinite] pointer-events-none" />
          
          {/* Header */}
          <div className="relative bg-gradient-to-r from-accent to-accent/90 text-accent-foreground p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-foreground/20 flex items-center justify-center ring-2 ring-accent-foreground/20 shadow-lg">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold">Val</div>
                <div className="text-xs opacity-80 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  Online Now
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-2 hover:bg-accent-foreground/10 rounded-lg transition-all duration-300 hover:scale-110"
                aria-label="Minimize"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-accent-foreground/10 rounded-lg transition-all duration-300 hover:scale-110 hover:rotate-90"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Intro Form - Shown before chat starts */}
              {showIntroForm ? (
                <div className="flex-1 flex flex-col p-6 bg-gradient-to-b from-muted/50 to-background">
                  <div className="flex-1 flex flex-col justify-center">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center mx-auto mb-4 ring-4 ring-accent/20 shadow-lg shadow-accent/20">
                        <Bot className="h-8 w-8 text-accent" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Hi, I'm Val! 👋
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        I'm here to help you grow your business online. Let's chat!
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="intro-name" className="text-sm font-medium text-foreground block mb-2">
                          What's your name? (optional)
                        </label>
                        <Input
                          id="intro-name"
                          value={introName}
                          onChange={(e) => setIntroName(e.target.value)}
                          onKeyDown={handleIntroKeyPress}
                          placeholder="Your first name"
                          className="w-full"
                        />
                      </div>
                      
                      <Button 
                        onClick={startChatWithName}
                        className="w-full group"
                        size="lg"
                      >
                        Start Chatting
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                      
                      <p className="text-xs text-muted-foreground text-center">
                        I can help with quotes, services info, or booking a free consultation.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-muted/30 to-background">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={cn(
                          "flex gap-2 animate-fade-in",
                          message.role === "user" ? "justify-end" : "justify-start"
                        )}
                      >
                        {message.role === "assistant" && (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center shrink-0 ring-1 ring-accent/20 shadow-sm">
                            <Bot className="h-4 w-4 text-accent" />
                          </div>
                        )}
                        <div
                          className={cn(
                            "max-w-[75%] rounded-2xl px-4 py-2.5 text-sm transition-all duration-300",
                            message.role === "user"
                              ? "bg-gradient-to-br from-accent to-accent/90 text-accent-foreground rounded-br-md shadow-lg shadow-accent/20"
                              : "bg-card border border-border/50 rounded-bl-md shadow-sm hover:shadow-md"
                          )}
                        >
                          {message.content}
                        </div>
                        {message.role === "user" && (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 ring-1 ring-border shadow-sm">
                            <User className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    ))}
                    {isLoading && (
                      <div className="flex gap-2 justify-start animate-fade-in">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center shrink-0 ring-1 ring-accent/20">
                          <Bot className="h-4 w-4 text-accent" />
                        </div>
                        <div className="bg-card border border-border/50 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                          <div className="flex gap-1.5">
                            <span className="w-2 h-2 bg-accent/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-2 h-2 bg-accent/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-2 h-2 bg-accent/60 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Quick Replies */}
                  {showQuickReplies && messages.length === 1 && !isLoading && (
                    <div className="px-4 pb-2 bg-background border-t border-border/50">
                      <p className="text-xs text-muted-foreground mb-2 pt-2">Quick questions:</p>
                      <div className="flex flex-wrap gap-2">
                        {quickReplies.map((reply) => (
                          <button
                            key={reply.label}
                            onClick={() => handleQuickReply(reply.message)}
                            className="px-3 py-1.5 text-xs bg-muted hover:bg-accent/10 text-foreground rounded-full border border-border hover:border-accent/30 transition-all duration-300 hover:shadow-sm hover:-translate-y-0.5"
                          >
                            {reply.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Input */}
                  <div className="p-4 border-t border-border/50 bg-background shrink-0">
                    <div className="flex gap-2">
                      <Input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Type a message..."
                        className="flex-1"
                        disabled={isLoading}
                      />
                      <Button
                        onClick={handleSendClick}
                        disabled={!input.trim() || isLoading}
                        size="icon"
                        className="shrink-0 shadow-lg shadow-primary/20"
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Powered by Valentine Digital
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
